import * as dte from "../../../DtEventTypes";
import {FacetTypes, FacetsScope} from "../../../facets/Facets";
import {HudLayer} from '../HudLayer';
import {HUD_LAYER} from "../HudLayerConstants";
import {PRIORITY} from "../../LabelCompositor";
import { DtConstants } from "../../../schema/DtConstants";
import { COLOR_SCHEME } from "../../../streams/StreamLabelColors";
import {PinUI} from "../../ui/PinUI";
import {DispatchEventOnHover, FocusOnHover, MouseInteractionsPlugin, SelectOnClick, UiCollision} from "../../ui/plugins";
import {AnimatedWorldPos3D} from "../../ui/positions";
import { ALERT_ICON, TICKET_ICON, NotificationDotUI } from "../../ui/NotificationDotUI";
import { TraceDragHandleUI } from "../../ui/TraceDragHandleUI";
import { getResourceUrl } from "../../../../globals";
import { TrackedElementsProvider } from "./TrackedElementsProvider";
import { DtMap } from "./DtMap";
import { TRACE_ELEMENT_TYPE } from "../../../connectivity/SystemVisualizer";

const { StreamStates, StreamStatePriority, StreamAlertState2State } = DtConstants;

export const DEFAULT_TICKET_COLOR = '#7960FF';
export function getMostFrequentCustomThemeColor(tickets, defaultModel) {
    let resultColor;
    const countsByColor = {}
    let maxCount = 0;
    for (const t of tickets) {
        const c = defaultModel.myData.dbId2ThemingColor?.[t.dbId];
        if (c) {
            const n = (countsByColor[c] = (countsByColor[c] || 0) + 1);
            if (n > maxCount) { maxCount = n; resultColor = c; }
        }
    }

    return resultColor;
}

/**
 * HUD layer that renders per-element labels (assets, connection state, tickets, alerts, trace handles) on the 3D
 * canvas. All data provisioning (which elements have labels, their stream/ticket/alert state, level/room facets)
 * lives in {@link TrackedElementsProvider}; this layer consumes it through eventing and owns only the label lifecycle,
 * composition, priorities, selection and trace-drag interaction.
 */
export class AssetsLayer extends HudLayer {
    /** @type {TrackedElementsProvider} Facility-owned element data (streams/alerts/tickets and the filtered set). */
    #data;
    labelById = new DtMap();
    priorities = Object.keys(PRIORITY).map(() => ([]));

    needsRedraw = false;

	#dragTarget = null;

    constructor(facility, hud, viewer) {
        super(HUD_LAYER.ASSETS.id, facility, hud, viewer);

        this.hasTrackingLabels = true;
        this.#data = facility.getTrackedElementsProvider();

        this.boundOnDataChanged = this.#onDataChanged.bind(this);
        this.boundOnDataPatched = this.#onDataPatched.bind(this);
		this.boundOnTraceDragStart = this.#onTraceDragStart.bind(this);
		this.boundOnTraceDragEnd = this.#onTraceDragEnd.bind(this);
    }

    dispose() {
        this.labelById.forEach((l) => l.dispose());
        this.labelById.clear();

        this.#data.removeEventListener(dte.DT_TRACKED_ELEMENTS_CHANGED_EVENT, this.boundOnDataChanged);
        this.#data.removeEventListener(dte.DT_TRACKED_ELEMENTS_PATCHED_EVENT, this.boundOnDataPatched);
        this.#data.deactivate(this);

		this.viewer.removeEventListener(dte.DT_TRACE_DRAG_BEGIN, this.boundOnTraceDragStart);
		this.viewer.removeEventListener(dte.DT_TRACE_DRAG_STOP, this.boundOnTraceDragEnd);
    }

    init() {
        this.#data.addEventListener(dte.DT_TRACKED_ELEMENTS_CHANGED_EVENT, this.boundOnDataChanged);
        this.#data.addEventListener(dte.DT_TRACKED_ELEMENTS_PATCHED_EVENT, this.boundOnDataPatched);

		this.viewer.addEventListener(dte.DT_TRACE_DRAG_BEGIN, this.boundOnTraceDragStart);
		this.viewer.addEventListener(dte.DT_TRACE_DRAG_STOP, this.boundOnTraceDragEnd);

        return this;
    }

    /** The provider rebuilt its element set / metadata: re-run update() (and recompose when asked). */
    #onDataChanged({ redraw }) {
        if (redraw) this.needsRedraw = true;
        this.invalidate();
    }

    /** The provider patched already-provided elements in place: update their labels without a full rebuild. */
    #onDataPatched({ patches }) {
        for (const { model, dbId, connState, alerts } of patches) {
            const label = this.labelById.get(model, dbId);
            if (!label) continue;

            if (connState !== undefined) {
                label.data.connState = connState;
                connState ? label.updateConnectionState(connState) : label.clearConnectionState();
            }

            if (alerts !== undefined) {
                label.data.alerts = alerts;
                alerts ? label.notifyAlerts(alerts) : label.clearAlert();
            }
        }

        // Deferred labels/rollups in other layers may reflect these changes, so force a recompose.
        this.needsRedraw = true;
    }

    onClusterTransition(type, layerPrefs, isClustered) { /* Do nothing and allow labels. */ }

    onIsolationChanged() { this.#data.onIsolationChanged(); }

    onModelChanged(event) { this.#data.onModelChanged(event); }

    onSelectionChanged() {
        const defaultModel = this.facility.getDefaultModel();
        let hasChanged = false;
        for (const label of this.labelById.values()) {
            const { model, dbId, tickets } = label.data;
            let selected = Boolean(model.selector?.isSelected(dbId));
            if (!selected && tickets && defaultModel) {
                selected = tickets.some((t) => defaultModel.selector?.isSelected(t.dbId));
            }

            if (label.isSelected !== selected) {
                hasChanged = true;
                label.setSelected(selected);
            }
        }

        if (hasChanged) this.invalidate();
    }

    async setVisible(visible) {
        const then = super.setVisible(visible);

        if (visible) {
            // immediately update elements for accurate assets while animating to full visibility.
            await this.#data.activate(this);
        } else {
            this.#data.deactivate(this);

            let hasVisibleLabels = false;
            for (const ui of this.labelById.values()) {
                if (ui.isVisible()) {
                    hasVisibleLabels = true;
                    break;
                }
            }

            if (!hasVisibleLabels) {
                // No reason to animate, just skip to the end.
                this.visibility.param.skipAnim();
            }

            // Re-compose just after turning invisible, to cause removal of deferred assets in other visible layers.
            await then;
            this.needsRedraw = true;
            this.invalidate();
        }

        return then;
    }

    compose(ctx, priority) {
        // needsRedraw will be cleared by HudLayers, no need to do it here.
        const uis = this.priorities[priority];

        if (priority === PRIORITY.OFF) {
            for (const ui of uis) {
                ui.setVisible(false);
            }
            return;
        }

        const facilityLevelItem = this.#data.getFacilityLevelItem();
        if (priority === PRIORITY.NORMAL && facilityLevelItem) {
            ctx.rollups.addDeferred(facilityLevelItem);
        }

        if (!uis.length) return;

        // Main composition extracted for testing purposes.
        doComposeAssetUIs(ctx, priority, uis);
    }

    update(ts) {
        if (!this.isVisible()) return;

        // Reset priority arrays
        this.priorities.forEach((uis) => { uis.length = 0; });

        const mark = (lbl, priority) => {
            this.priorities[priority].push(lbl);
            if (lbl.data.priority === priority) return;

            lbl.data.priority = priority;
        }

        const defaultModel = this.facility.getDefaultModel();
        const toRemove = this.labelById.clone();
        const themedFacetId = this.facility.facetsManager.getThemedFacetDef(FacetsScope.Tickets)?.id;
        const isSingleElementTrace = this.#data.isSingleTracedElement();

        for (const [model, byDbId] of this.#data.getElements().map) {
            const it = model.getObjectTree();

            for (const [dbId, metadata] of byDbId) {
                const { tickets, alerts, connState, isSpatial, tracedType } = metadata;

                // no need to remove.
                toRemove.delete(model, dbId);

                let label = this.labelById.get(model, dbId);
                if (!label) {
                    label = new ElementUI(this.hud, this.layerID, model, dbId);

                    // even if asset labels for location will always be deferred, we use the mechanisms of the label
                    // to hold and manage metadata to be rolled up. This can be optimized later, but at least for now
                    // we disconnect it from the DOM to remove unnecessary DOM re-render / re-styling.
                    if (!isSpatial) label.init(this.dom);

                    let selected = Boolean(model.selector?.isSelected(dbId));
                    if (!selected && tickets && defaultModel) {
                        selected = tickets.some((t) => defaultModel.selector?.isSelected(t.dbId));
                    }

                    label.setSelected(selected);
                    this.labelById.set(model, dbId, label);
                }

                // here all metadata is relevant to the UI label (alert, tickets, connState, room & level facets)
                Object.assign(label.data, metadata);

                // Prevent traced elements from being hidden
                if (!tracedType && it.isNodeHidden(dbId)) {
                    mark(label, PRIORITY.OFF);
                    continue;
                }

                connState ? label.updateConnectionState(connState) : label.clearConnectionState();

                tickets ? label.notifyTickets(tickets, themedFacetId) : label.clearTicket();

                alerts ? label.notifyAlerts(alerts) : label.clearAlert();

                const showTraceHandle = !isSpatial && !isSingleElementTrace && tracedType >= TRACE_ELEMENT_TYPE.END;
                showTraceHandle ? label.notifyTraceDragHandle(tracedType) : label.clearTraceDragHandle();

                if (label.isSelected || tracedType) {
                    // If the label is selected or is part of a trace, it should be on top.
                    mark(label, PRIORITY.TOPMOST);
                } else {
                    mark(label, (tickets || alerts) ? PRIORITY.ABOVE : PRIORITY.NORMAL);
                }
            }
        }

        // Cleanup unseen label
        toRemove.forEach((label, model, dbId) => {
            label.dispose();
            this.labelById.delete(model, dbId);
        })

        // Since there's still some relative difference in importance between NORMAL labels, we sort them.
        // at the moment, re-sorting within the same priority doesn't trigger a redraw.
        this.priorities[PRIORITY.NORMAL].sort(byDescConnectionState);

        this.isDirty = false;

        // Some changes we make will directly affect layer composition (ex: label priority) and some will indirectly
        // affect how deferred labels are shown (ex: connection state, tickets, alerts). So while the UI for the asset
        // label is immediately updated, for now, we force a re-compose to update the deferred labels in other layers.
        // note: it's quicker to redraw than diffing all metadata since most typical updates will require a redraw.
        return true;
    }

    uis() {
        return this.labelById.values();
    }

	// Ideally this should be coming from plugins as "OnTraceHover"
	// but right now it's inverted since the event comes from the ToolController without going through the plugins
	// eg. ToolController -> HUD -> AssetsLayer -> ElementUI (trace label) -> DispatchEvent
	handleMouseMove(event) {
		if (this.#dragTarget) {
			this.#dragTarget.dragMove(event);
			return true; // stop propagation
		}
	}

	#onTraceDragStart(event) {
		this.#dragTarget = this.labelById.get(event.model, event.dbId);
		this.labelById.delete(event.model, event.dbId);

		this.invalidate();
	}

	#onTraceDragEnd(event) {
		this.#dragTarget?.dispose();
		this.#dragTarget = null;

		this.invalidate();
	}
}

export function doComposeAssetUIs(ctx, priority, uis) {
    // Since UIs can be colliding without being in the same rollup facet, we still perform composition
    // in order of priority, so higher priority UIs have less chance of being deferred than lower priority UIs.
    for (const ui of uis) {
        // if the UI location(s) have been pulled back, or if the asset itself is a location.
        if (ui.data.isSpatial || (ctx.rollups.shouldBeDeferred(ui.data) && priority !== PRIORITY.TOPMOST)) {
            ctx.rollups.addDeferred(ui.data);
            ui.setVisible(false);
            continue;
        }

        const target = ui.getCollisionTarget();
        // Mark the target as available to be pulled back to a rollup if a future target requests it.
        target.withPullback = priority !== PRIORITY.TOPMOST;
        if (!ctx.onScreen(target)) {
            // Being offscreen isn't enough to defer a label, but they still can be.
            ctx.rollups.addOffscreen(target);
            ui.setVisible(false);
            continue;
        }

        // As soon as we place it, the compositor manages visibility to retain the ability to pull back UIs.
        ctx.placeAsset(target);
    }
}

export function getStateColor(state) {
    switch (state) {
        case StreamStates.Alert:
            return COLOR_SCHEME.error;
        case StreamStates.Warning:
            return COLOR_SCHEME.warning;
        case StreamStates.Normal:
            return COLOR_SCHEME.success;
        case StreamStates.NoData:
        case StreamStates.Offline:
            return '#999'; // Temporarily switch to Figma offline color for higher contrast.
        //return COLOR_SCHEME.offline;
        default:
            console.warn('unexpected state for asset label', state);
    }
}

function byDescConnectionState(a, b) {
    if (!a.data?.connState) return 1;
    if (!b.data?.connState) return -1;
    return StreamStatePriority[b.data.connState.state] - StreamStatePriority[a.data.connState.state];
}

const ESTIMATED_SIZE = { width: 40, height: 39 };
const ESTIMATED_HEIGHT_OFFSET = 8; // half of the height of trace drag handle
export class ElementUI extends PinUI {
    static spriteUrl;

    constructor(hud, layer, model, dbId) {
        ElementUI.spriteUrl ??= getResourceUrl('res/ui/tandem-cats.svg');

        const tandemCategory = model.getElementTandemCategory(dbId);
        super(hud, layer,
            new AnimatedWorldPos3D(model, dbId),
            tandemCategory && `<use href="${ElementUI.spriteUrl}#${tandemCategory}" fill="black" height="12" width="12" transform="translate(-6,-6)"/>`);

        this.data.dbId = dbId;
        this.data.model = model;

		const it = model.getObjectTree();
		const name = it.getNodeName(dbId);
		this.innerDom.setAttribute('data-test-id', `name:${name}`);

        this.eventTarget = this.iconHost;
        this.plugins.push(new UiCollision());
		this.plugins.push(new MouseInteractionsPlugin());
        this.plugins.push(new SelectOnClick());
        this.plugins.push(new DispatchEventOnHover());
		this.plugins.push(new FocusOnHover());
    }

    notifyTraceDragHandle(tracedType) {
        this.traceDragHandle ??= new TraceDragHandleUI(this);
        this.traceDragHandle.updateTracedType(tracedType);
        this.traceDragHandle.setActive(true);
    }

    clearTraceDragHandle() {
        this.traceDragHandle?.setActive(false);
    }

    updateConnectionState({ state }) {
        // Color is solely based on worst stream state, if applicable
        this.setBGColor(getStateColor(state));
    }

    clearConnectionState() {
        this.data.connState = null; // the Object.assign won't clear
        this.setBGColor('');
    }

    notifyTickets(tickets, currentThemedFacetId) {
        const onTicketDotClick = () => {
            this.hud.viewer.dispatchEvent({
                type: dte.DT_SHOW_ASSET_DETAIL_EVENT,
                model: this.data.model,
                dbId: this.data.dbId,
                notificationType: 'tickets',
            });
        };
        this.ticketDot ??= new NotificationDotUI(this, TICKET_ICON, { top: 'top', left: 'left' }, onTicketDotClick);
        this.ticketDot.setCount(tickets.length);

        // use custom themed color to display tickets if there is one, otherwise default to priority color
        // in the case multiple tickets, we use the highest priority color (if colored by priority) or the most frequent custom theme color
        if (tickets.length) {
            const defaultModel = this.data.model.facility.getDefaultModel();
            let ticketDotColor = tickets[0].priorityColor;
            if (currentThemedFacetId) {
                ticketDotColor = defaultModel.myData.dbId2ThemingColor?.[tickets[0].dbId];
                if (currentThemedFacetId !== FacetTypes.priority) {
                    ticketDotColor = getMostFrequentCustomThemeColor(tickets, defaultModel);
                }
            }
            this.ticketDot.setBGColor(ticketDotColor ?? DEFAULT_TICKET_COLOR);
        }
    }

    clearTicket() {
        this.data.tickets = null; // the Object.assign won't clear
        this.ticketDot?.setCount(0);
    }

    notifyAlerts(alerts) {
        const onAlertDotClick = () => {
            this.hud.viewer.dispatchEvent({
                type: dte.DT_SHOW_ASSET_DETAIL_EVENT,
                model: this.data.model,
                dbId: this.data.dbId,
                notificationType: 'alerts',
            });
        };
        const state = alerts[0].alertState;
        const count = alerts.length;
        const color = getStateColor(StreamAlertState2State[state]);

        this.alertDot ??= new NotificationDotUI(this, ALERT_ICON, { top: 'top', left: 'right' }, onAlertDotClick);
        this.alertDot.setBGColor(color);
        this.alertDot.setCount(count);
    }

    clearAlert() {
        this.data.alerts = null; // the Object.assign won't clear
        this.alertDot?.setCount(0);
    }

	dragStart() {
        this.clearAlert();
        this.clearTicket();
        this.clearConnectionState();
        this.hideIcon();

		const { dbId, model } = this.data;
		this.hud.viewer.dispatchEvent({ type: dte.DT_TRACE_DRAG_BEGIN, dbId, model });
	}

	dragMove(event) {
		// Check if the left mouse button is still pressed and stop dragging if it's not
		if (event.buttons !== 1) {
			this.dragStop();
			return;
		}

		// Move the label to the mouse position, aligned with the trace drag handle
		const viewer = this.hud.viewer;
		viewer.toolController.__clientToCanvasCoords(event);
		this.onCanvas2DChange({ x: event.canvasX, y: event.canvasY - ESTIMATED_HEIGHT_OFFSET });

		// Get the dbId and model at the mouse position
		const vImpl = viewer.impl;
		const idRes = [];
		const vpVec = vImpl.clientToViewport(event.canvasX, event.canvasY);
		const dbId = vImpl.renderer().idAtPixel(vpVec.x, vpVec.y, idRes);
		const model = vImpl.modelQueue().findModel(idRes[1]);

		if (dbId && model) {
			viewer.dispatchEvent({ type: dte.DT_TRACE_DRAG_HOVER, dbId, model });
		}
	}

	dragStop() {
		this.hud.viewer.dispatchEvent({ type: dte.DT_TRACE_DRAG_STOP });
	}

    getEstimatedSize() { return ESTIMATED_SIZE; }
}
